import React from "react";

import ArbitrumIcon from "@/icons/token/arbitrum-icon";
import Re7Icon from "@/icons/token/re7-icon";

import { AvailableTokens } from "./token-icons";

const TOKEN_BRAND_COLORS: Record<AvailableTokens, string> = {
  KNOX: "#CCD7C0",
  "ETH+": "#83C7FF",
  eUSD: "#ffffff",
  weETH:
    "linear-gradient(13.99deg, #29BCFA 11.17%, #6464E4 42.34%, #B45AFA 83.66%)",
  ezETH: "#000000",
  USDe: "#000000",
  sUSDe: "#ffffff",
  USDT: "#26A17B",
  USDB: "#FCFC03",
  MUSD: "linear-gradient(270deg, #68FFFF 5.56%, #68FF9D 105.56%)",
  ETH: "#627EEA",
  WETH: "#627EEA",
  ARB: "#213147",
  USDC: "#2775CA",
  "USDC.e": "#2775CA",
  GMX: "#FFFFFF",
  WBTC: "#F7931A",
  PENDLE: "#EFF1FC",
  RDNT: "linear-gradient(180deg, #00FFAA 0%, #5E02FA 100%)",
  BLAST: "#FCFC03",
  "PT-weETH-27JUN2024":
    "linear-gradient(13.99deg, #29BCFA 11.17%, #6464E4 42.34%, #B45AFA 83.66%)",
  "PT-weETH-26DEC2024":
    "linear-gradient(13.99deg, #29BCFA 11.17%, #6464E4 42.34%, #B45AFA 83.66%)",
  "PT-USDe-25JUL2024": "#000000",
  "PT-USDe-29AUG2024": "#000000",
  "PT-weETH-26SEP2024":
    "linear-gradient(13.99deg, #29BCFA 11.17%, #6464E4 42.34%, #B45AFA 83.66%)",
  "PT-ezETH-26SEP2024": "#000000",
  "PT-ezETH-26DEC2024": "#000000",
  "PT-amphrETH-26SEP2024": "#E41A1A",
  "PT-pzETH-26SEP2024": "#000000",
  "PT-uniETH-26SEP2024": "#ffffff",
  pzETH: "#000000",
  uniETH: "#ffffff",
  amphrETH: "#E41A1A",
  "PT-rsETH-26SEP2024": "#075A5A",
  rsETH: "#075A5A",
  "PT-agETH-26DEC2024": "#075A5A",
  agETH: "#075A5A",
  amphrLRT: "#15164E",
  "PT-amphrLRT-26SEP2024": "#15164E",
  "PT-rsETH-26DEC2024": "#075A5A",
  "PT-amphrLRT-26DEC2024": "#15164E",
  "PT-EBTC-26DEC2024": "#0E0B31",
  "PT-pufETH-26DEC2024":
    "linear-gradient(131.74deg, #874FFF -0.83%, #36AFE2 36.14%, #41FF54 73.11%)",
  Re7LRT: "#E41A1A",
  "PT-Re7LRT-26DEC2024": "#E41A1A",
  "PT-amphrETH-26DEC2024": "#E41A1A",
  "PT-uniETH-26DEC2024": "#ffffff",
  "PT-weETHs-26DEC2024": "#0E0B31",
  eBTC: "#0E0B31",
  pufETH:
    "linear-gradient(131.74deg, #874FFF -0.83%, #36AFE2 36.14%, #41FF54 73.11%)",
  uniBTC: "#FFFFFF",
  weETHs: "#0E0B31",
  "PT-cornLBTC-26DEC2024": "#162E2F",
  LBTC: "#162E2F",
  "PT-corn-pumpBTC-26DEC2024": "#F7931A",
  "PT-corn-uniBTC-26DEC2024": "#FFFFFF",
  "PT-USDe-28NOV2024": "#ffffff",
  "PT-gUSDC-6DEC2024": "#ffffff",
  USDE: "#ffffff",
  "PT-gUSDC-26DEC2024": "#ffffff",
  "PT-LBTC-27MAR2025": "#162E2F",
  "PT-corn-eBTC-27MAR2025": "#0E0B31",
  "PT-rETH-26JUN2025": "#000000",
  "PT-wstETH-26JUN2025": "#00A3FF",
  "PT-rsETH-26JUN2025": "#075A5A",
  "PT-uniETH-26JUN2025": "#ffffff",
  "PT-weETH-26JUN2025":
    "linear-gradient(13.99deg, #29BCFA 11.17%, #6464E4 42.34%, #B45AFA 83.66%)",
};

export const getTokenBrandColor = (token?: string) =>
  TOKEN_BRAND_COLORS[token as AvailableTokens];

// Unique token image
const TOKEN_PNG: Partial<Record<AvailableTokens, string>> = {
  ezETH: "/images/token-image/ezeth.png",
  "PT-ezETH-26SEP2024": "/images/token-image/ezeth.png",
  "PT-ezETH-26DEC2024": "/images/token-image/ezeth.png",
  ARB: "/images/token-image/arb.png",
  GMX: "/images/token-image/gmx.png",
  "PT-pzETH-26SEP2024": "/images/token-image/pzeth.png",
  "PT-uniETH-26SEP2024": "/images/token-image/unieth.png",
  pzETH: "/images/token-image/pzeth.png",
  uniETH: "/images/token-image/unieth.png",
  "PT-rsETH-26SEP2024": "/images/token-image/rseth.png",
  rsETH: "/images/token-image/rseth.png",
  "PT-amphrLRT-26SEP2024": "/images/token-image/amphrlrt.png",
  amphrLRT: "/images/token-image/amphrlrt.png",
  "PT-rsETH-26DEC2024": "/images/token-image/rseth.png",
  eBTC: "/images/token-image/ebtc.png",
  "PT-EBTC-26DEC2024": "/images/token-image/ebtc.png",
  pufETH: "/images/token-image/pufeth.png",
  "PT-pufETH-26DEC2024": "/images/token-image/pufeth.png",
  uniBTC: "/images/token-image/unibtc.png",
  "PT-uniETH-26DEC2024": "/images/token-image/unieth.png",
  weETHs: "/images/token-image/weeths.png",
  "PT-weETHs-26DEC2024": "/images/token-image/weeths.png",
  "PT-amphrLRT-26DEC2024": "/images/token-image/amphrlrt.png",
  LBTC: "/images/token-image/lbtc.png",
  "PT-cornLBTC-26DEC2024": "/images/token-image/lbtc.png",
  "PT-rETH-26JUN2025": "/images/token-image/reth.png",
  "PT-wstETH-26JUN2025": "/images/token-image/wsteth.png",
};

export const getTokenPng = (token?: string) => {
  return TOKEN_PNG[token as AvailableTokens];
};

// Unique token image
const SUB_TOKENS: Partial<Record<AvailableTokens, React.FC>> = {
  "USDC.e": ArbitrumIcon,
  Re7LRT: Re7Icon,
  "PT-Re7LRT-26DEC2024": Re7Icon,
};

export const getSubToken = (token?: string) => {
  return SUB_TOKENS[token as AvailableTokens];
};

const LIGHT_TOKENS: AvailableTokens[] = [
  "ETH",
  "WETH",
  "weETH",
  "USDe",
  "USDT",
  "ETH",
  "USDC",
  "USDC.e",
  "WBTC",
  "RDNT",
  "PT-weETH-27JUN2024",
  "PT-weETH-26DEC2024",
  "PT-USDe-25JUL2024",
  "PT-USDe-29AUG2024",
  "PT-weETH-26SEP2024",
  "PT-amphrETH-26SEP2024",
  "amphrETH",
  "agETH",
  "PT-agETH-26DEC2024",
  "Re7LRT",
  "PT-Re7LRT-26DEC2024",
  "PT-amphrETH-26DEC2024",
  "PT-corn-eBTC-27MAR2025",
];

export const getIsLightToken = (token: string) =>
  LIGHT_TOKENS.includes(token as AvailableTokens);

// for api/image
const SHARE_TOKEN_PATH: Record<AvailableTokens, string | undefined> = {
  KNOX: `/images/token/knox.svg`,
  "ETH+": "/images/token/eth+.svg",
  eUSD: "/images/token/susde.svg",
  weETH: "/images/token/weeth.svg",
  ezETH: "/images/token/ezeth.svg",
  USDe: "/images/token/usde.svg",
  sUSDe: "/images/token/susde.svg",
  USDT: "/images/token/usdt.svg",
  USDB: "/images/token/usdb.svg",
  MUSD: "/images/token/musd.svg",
  ETH: "/images/token/eth.svg",
  WETH: "/images/token/weth.svg",
  ARB: "/images/token/arb.svg",
  USDC: "/images/token/usdc.svg",
  "USDC.e": "/images/token/usdc.svg",
  GMX: "/images/token/gmx.svg",
  WBTC: "/images/token/wbtc.svg",
  PENDLE: "/images/token/pendle.svg",
  RDNT: "/images/token/rdnt.svg",
  BLAST: "/images/token/blast.svg",
  pzETH: "/images/token/pzeth.svg",
  uniETH: "/images/token/unieth.svg",
  amphrETH: "/images/token/eth.svg",
  "PT-weETH-27JUN2024": "/images/token/weeth.svg",
  "PT-weETH-26DEC2024": "/images/token/weeth.svg",
  "PT-USDe-25JUL2024": "/images/token/usde.svg",
  "PT-USDe-29AUG2024": "/images/token/usde.svg",
  "PT-weETH-26SEP2024": "/images/token/weeth.svg",
  "PT-ezETH-26SEP2024": "/images/token/ezeth.svg",
  "PT-ezETH-26DEC2024": "/images/token/ezeth.svg",
  "PT-amphrETH-26SEP2024": "/images/token/eth.svg",
  "PT-pzETH-26SEP2024": "/images/token/pzeth.svg",
  "PT-uniETH-26SEP2024": "/images/token/unieth.svg",
  "PT-rsETH-26SEP2024": "/images/token/rseth.svg",
  rsETH: "/images/token/rseth.svg",
  "PT-agETH-26DEC2024": "/images/token/ageth.svg",
  "PT-amphrLRT-26SEP2024": "/images/token/amphrlrt.svg",
  "PT-rsETH-26DEC2024": "/images/token/rseth.svg",
  agETH: "/images/token/ageth.svg",
  amphrLRT: "/images/token/amphrlrt.svg",
  "PT-amphrETH-26DEC2024": "/images/token/eth.svg",
  "PT-amphrLRT-26DEC2024": "/images/token/amphrlrt.svg",
  "PT-uniETH-26DEC2024": "/images/token/unieth.svg",
  "PT-EBTC-26DEC2024": "/images/token/ebtc.svg",
  "PT-pufETH-26DEC2024": "/images/token/pufeth.svg",
  "PT-Re7LRT-26DEC2024": "/images/token/weeth.svg",
  "PT-weETHs-26DEC2024": "/images/token/weeths.svg",
  eBTC: "/images/token/ebtc.svg",
  pufETH: "/images/token/pufeth.svg",
  Re7LRT: "/images/token/weeth.svg",
  weETHs: "/images/token/weeths.svg",
  uniBTC: "/images/token/unibtc.svg",
  "PT-cornLBTC-26DEC2024": "/images/token/lbtc.svg",
  LBTC: "/images/token/lbtc.svg",
  "PT-corn-pumpBTC-26DEC2024": "/images/token/pumpBTC.svg",
  "PT-corn-uniBTC-26DEC2024": "/images/token/unibtc.svg",
  "PT-USDe-28NOV2024": "/images/token/usde.svg",
  "PT-gUSDC-6DEC2024": "/images/token/usdc.svg",
  USDE: "/images/token/usde.svg",
  "PT-gUSDC-26DEC2024": "/images/token/usdc.svg",
  "PT-LBTC-27MAR2025": "/images/token/lbtc.svg",
  "PT-corn-eBTC-27MAR2025": "/images/token/ebtc.svg",
  "PT-rETH-26JUN2025": "/images/token/eth.svg",
  "PT-rsETH-26JUN2025": "/images/token/rseth.svg",
  "PT-uniETH-26JUN2025": "/images/token/unieth.svg",
  "PT-weETH-26JUN2025": "/images/token/weeth.svg",
  "PT-wstETH-26JUN2025": "/images/token/eth.svg",
} as const;

export const getShareTokenPath = (token?: string) => {
  return SHARE_TOKEN_PATH[token as AvailableTokens];
};
